import { OdemeDenemeleri } from './OdemeDenemeleri';
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { api, type Me, type WsIs, type WsSiparisSatiri, type WsTur } from './api';
import { Cekmece, KisiSayfasi } from './WebSitesiKisi';
import { Genel } from './WebSitesiGenel';
import { Siparisler, Talepler, Uyeler } from './WebSitesiListeler';
import { Bildirim, Hata, useIpucu, useVeri } from './WebSitesiOrtak';
import { WS } from './metin/web-sitesi';
import { ESKI_YONLENDIRME, durumdanUrl, git as durumGit, urldenDurum, type Durum, type OdemeSuzgeci, type Sekme, type SiparisSuzgeci, type TalepSuzgeci, type UyeSuzgeci } from './web-sitesi-durum';
import { tarihSaatBae } from './web-sitesi-bicim';
import './web-sitesi.css';
import { useYol } from './yonlendirme';

/**
 * Web sitesi bölümü (Ürün Sahibi, 07.10.2026, onaylı taslak A tam sürüm).
 *
 * Dört sekme: Genel bakış, Talepler, Üyeler, Siparişler. Genel bakıştaki her sayı ve grafik ilgili
 * sekmeyi süzülmüş açar (`web-sitesi-durum.ts`). Satış raporu ve Katalog Satış bölümüne, Alacaklar
 * ve faturalar Finans bölümüne taşındı; eski `?sekme=` değerleri oraya yönleniyor.
 */

const SEKME_SIRASI: Sekme[] = ['genel', 'talepler', 'uyeler', 'siparisler', 'odemeler'];

export function WebSitesi({ me }: { me: Me }) {
  const { git: yolaGit } = useYol();
  const webYetkisi = me.permissions.includes('web_intake:read');
  const odemeYetkisi = me.permissions.includes('finance:read') && me.permissions.includes('commerce:finance_read');
  const [d, setD] = useState<Durum>(() => {
    const initial = urldenDurum(window.location.search);
    return !webYetkisi && odemeYetkisi ? { ...initial, sekme: 'odemeler', kisi: null } : initial;
  });
  const [cekmece, setCekmece] = useState<{ tur: WsTur; ref: string } | null>(() => {
    const q = new URLSearchParams(window.location.search);
    const ref = q.get('siparis') ?? '';
    return webYetkisi && q.get('sekme') === 'siparisler' && /^[A-Za-z0-9-]{1,80}$/.test(ref) ? { tur: 'sip', ref } : null;
  });
  /** Çekmeceden bir karar verilince artar: liste ve genel bakış yeniden okunur (17.10.2026). */
  const [degisim, setDegisim] = useState(0);
  const ov = useVeri(() => webYetkisi ? api.webPanelOverview() : Promise.resolve(null), [webYetkisi]);
  const ipucu = useIpucu();

  // Eski sekme adresleri (Satış raporu, Katalog, Alacaklar): yeni yerlerine.
  useEffect(() => {
    const eski = ESKI_YONLENDIRME[new URLSearchParams(window.location.search).get('sekme') ?? ''];
    if (eski && 'yol' in eski) yolaGit(eski.yol);
  }, [yolaGit]);

  // Adres çubuğu durumu izler: sayfa yenilenince ya da bağlantı paylaşılınca aynı görünüm açılır.
  useEffect(() => {
    const url = `${window.location.pathname}${durumdanUrl(d)}`;
    if (url !== `${window.location.pathname}${window.location.search}`) window.history.replaceState(null, '', url);
  }, [d]);

  // Genel bakışa her dönüşte taze sayı: karar verilmiş ya da sipariş değişmiş olabilir.
  const yenileOv = ov.yenile;
  useEffect(() => {
    if (webYetkisi && d.sekme === 'genel' && !d.kisi) yenileOv();
  }, [d.sekme, d.kisi, yenileOv, webYetkisi]);

  const git = (hedef: string) => {
    if (hedef.startsWith('yol:')) {
      yolaGit(hedef.slice(4));
      return;
    }
    setCekmece(null);
    setD((o) => durumGit(o, hedef));
    window.scrollTo({ top: 0 });
  };
  const setOdeme: Dispatch<SetStateAction<OdemeSuzgeci>> = (f) => setD((o) => ({ ...o, odeme: typeof f === 'function' ? f(o.odeme) : f }));
  const setTalep: Dispatch<SetStateAction<TalepSuzgeci>> = (f) => setD((o) => ({ ...o, talep: typeof f === 'function' ? f(o.talep) : f }));
  const setUye: Dispatch<SetStateAction<UyeSuzgeci>> = (f) => setD((o) => ({ ...o, uye: typeof f === 'function' ? f(o.uye) : f }));
  const setSip: Dispatch<SetStateAction<SiparisSuzgeci>> = (f) => setD((o) => ({ ...o, sip: typeof f === 'function' ? f(o.sip) : f }));
  const isAc = (i: WsIs) => setCekmece({ tur: i.tur, ref: i.reference });
  const siparisAc = (o: WsSiparisSatiri) => setCekmece({ tur: 'sip', ref: o.reference });

  const sayilar: Record<Sekme, number | null> = {
    genel: null,
    odemeler: null,
    talepler: ov.veri?.tabs.requests ?? null,
    uyeler: ov.veri?.tabs.members ?? null,
    siparisler: ov.veri?.tabs.orders ?? null,
  };
  const aktif = (s: Sekme) => d.sekme === s;

  return (
    <section className="ws" data-testid="web-sitesi" {...ipucu.ozellikler}>
      <nav className="subtabs maintabs" aria-label={WS.sekmelerAria} role="tablist" data-testid="ws-sekmeler">
        {SEKME_SIRASI.filter(s => s === 'odemeler' ? odemeYetkisi : webYetkisi).map((s) => (
          <button key={s} type="button" role="tab" data-testid={`ws-sekme-${s}`} aria-selected={aktif(s)} onClick={() => git(`tab:${s}`)}>
            {WS.sekme[s]}
            {sayilar[s] !== null ? <span className="n">{sayilar[s]}</span> : null}
          </button>
        ))}
      </nav>
      <p className="moved" data-testid="ws-tasindi">
        <span><b>{WS.tasindi.satisBir}</b> {WS.tasindi.ve} <b>{WS.tasindi.satisIki}</b> {WS.tasindi.satisSon}</span>
        <span><b>{WS.tasindi.finans}</b> {WS.tasindi.finansSon}</span>
      </p>
      {webYetkisi && d.kisi ? <KisiSayfasi externalId={d.kisi} me={me} git={git} kararaGit={d.kararaGit} /> : null}
      {webYetkisi && !d.kisi && d.sekme === 'genel' ? (
        ov.veri ? (
          <Genel o={ov.veri} git={git} ac={isAc} secili={cekmece ? `${cekmece.tur}:${cekmece.ref}` : null} />
        ) : ov.hata ? (
          <Hata mesaj={ov.hata} yenile={ov.yenile} />
        ) : (
          <p>{WS.yukleniyor}</p>
        )
      ) : null}
      {webYetkisi && !d.kisi && d.sekme === 'talepler' ? <Talepler s={d.talep} setS={setTalep} ac={isAc} tazele={degisim} /> : null}
      {webYetkisi && !d.kisi && d.sekme === 'uyeler' ? <Uyeler s={d.uye} setS={setUye} git={git} /> : null}
      {webYetkisi && !d.kisi && d.sekme === 'siparisler' ? <Siparisler me={me} s={d.sip} setS={setSip} ac={siparisAc} git={git} /> : null}
      {d.sekme === 'odemeler' ? odemeYetkisi ? <OdemeDenemeleri s={d.odeme} setS={setOdeme} siparisYetkisi={webYetkisi} /> : <p role="alert">{WS.odeme.yetki}</p> : null}
      {ov.veri ? <p className="foot" data-testid="ws-olcum">{WS.olcum(tarihSaatBae(ov.veri.measuredAt))}</p> : null}
      {webYetkisi && cekmece ? <Cekmece tur={cekmece.tur} reference={cekmece.ref} me={me} git={git} kapat={() => setCekmece(null)} degisti={() => { setDegisim((n) => n + 1); ov.yenile(); }} /> : null}
      {ipucu.katman}
      <Bildirim metin={null} />
    </section>
  );
}
